/**
 * Personel Paneli Dashboard Servis Katmanı
 *
 * Dashboard istatistikleri, aktiviteler ve follow-up işlemlerini yönetir.
 * Supabase client üzerinden RLS korumalı sorgular çalıştırır.
 *
 * @module staffService
 */

import { supabase } from '../lib/supabase';

const staffService = {
  // ===========================================================
  // Dashboard İstatistikleri
  // ===========================================================

  /**
   * Personel dashboard'u için özet istatistikleri döndürür.
   * @returns {{ data: object|null, error: object|null }}
   */
  async getDashboardStats() {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      // Paralel olarak tüm istatistikleri çek
      const [
        customersResult,
        propertiesResult,
        blogPostsResult,
        activitiesResult,
        todayFollowUpsResult,
      ] = await Promise.all([
        // Toplam müşteri sayısı
        supabase
          .from('customers')
          .select('id', { count: 'exact', head: true }),

        // Toplam aktif ilan sayısı
        supabase
          .from('properties')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true),

        // Toplam blog yazısı sayısı
        supabase
          .from('blog_posts')
          .select('id', { count: 'exact', head: true }),

        // Son 30 gün içindeki aktivite sayısı
        supabase
          .from('customer_activities')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),

        // Bugünün follow-up'ları
        supabase
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .gte('next_follow_up', todayISO)
          .lt('next_follow_up', new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()),
      ]);

      return {
        data: {
          totalCustomers: customersResult.count || 0,
          totalProperties: propertiesResult.count || 0,
          totalBlogPosts: blogPostsResult.count || 0,
          recentActivities: activitiesResult.count || 0,
          todayFollowUps: todayFollowUpsResult.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('getDashboardStats error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Son Aktiviteler
  // ===========================================================

  /**
   * Son müşteri aktivitelerini getirir.
   * @param {number} [limit=10] - Maksimum kayıt sayısı
   * @returns {{ data: array|null, error: object|null }}
   */
  async getRecentActivities(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('customer_activities')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email)
        `)
        .order('activity_date', { ascending: false })
        .limit(limit);

      return { data, error };
    } catch (error) {
      console.error('getRecentActivities error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Bugünün Follow-up'ları
  // ===========================================================

  /**
   * Bugün follow-up yapılması gereken müşterileri getirir.
   * @returns {{ data: array|null, error: object|null }}
   */
  async getTodayFollowUps() {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();
      const tomorrowISO = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('customers')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          status,
          next_follow_up,
          notes,
          staff_id
        `)
        .gte('next_follow_up', todayISO)
        .lt('next_follow_up', tomorrowISO)
        .order('next_follow_up', { ascending: true });

      return { data, error };
    } catch (error) {
      console.error('getTodayFollowUps error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Gecikmiş Follow-up'lar
  // ===========================================================

  /**
   * Gecikmiş follow-up'ları getirir (bugünden önceki).
   * @returns {{ data: array|null, error: object|null }}
   */
  async getOverdueFollowUps() {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const { data, error } = await supabase
        .from('customers')
        .select(`
          id,
          first_name,
          last_name,
          email,
          phone,
          status,
          next_follow_up,
          notes,
          staff_id
        `)
        .lt('next_follow_up', todayISO)
        .not('status', 'eq', 'closed')
        .order('next_follow_up', { ascending: true });

      return { data, error };
    } catch (error) {
      console.error('getOverdueFollowUps error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Durum İstatistikleri
  // ===========================================================

  /**
   * Müşteri durum istatistiklerini getirir.
   * @returns {{ data: object|null, error: object|null }}
   */
  async getCustomerStatusStats() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('status');

      if (error) throw error;

      // Durumları grupla
      const stats = {
        new: 0,
        contacted: 0,
        interested: 0,
        not_interested: 0,
        closed: 0,
      };

      data?.forEach(customer => {
        if (customer.status && stats.hasOwnProperty(customer.status)) {
          stats[customer.status]++;
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      console.error('getCustomerStatusStats error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Aktivite İstatistikleri
  // ===========================================================

  /**
   * Belirli tarih aralığındaki aktivite istatistiklerini getirir.
   * @param {Date} startDate - Başlangıç tarihi
   * @param {Date} endDate - Bitiş tarihi
   * @returns {{ data: object|null, error: object|null }}
   */
  async getActivityStats(startDate, endDate) {
    try {
      const { data, error } = await supabase
        .from('customer_activities')
        .select('activity_type, activity_date')
        .gte('activity_date', startDate.toISOString())
        .lte('activity_date', endDate.toISOString());

      if (error) throw error;

      // Aktivite tiplerini grupla
      const stats = {
        call: 0,
        email: 0,
        meeting: 0,
        note: 0,
        property_showing: 0,
        follow_up: 0,
        total: data?.length || 0,
      };

      data?.forEach(activity => {
        if (activity.activity_type && stats.hasOwnProperty(activity.activity_type)) {
          stats[activity.activity_type]++;
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      console.error('getActivityStats error:', error);
      return { data: null, error };
    }
  },
};

export default staffService;
